import React,{FC, useEffect, useMemo, useState} from "react";
import { Card, Spinner, Tab, Tabs} from "react-bootstrap";
import { getHistoricalTradeData } from "../core/_request";
import { getNameByISO3, shortenLargeNumber } from "../../../../libs/misc";
import { useLoading } from "../../../../contexts/LoadingContext";
import TradePartnerDropdown from "./TradePartnerDropdown";
import ChartWithShare from "../../../../../ui-kit/ChartWithShare";

interface ExtendedHighchartCreditInterface{
  credits:any
}
type ByProductChartCardProps = {
  params:{
    rep: string;
    par: string;
    tradeflow:string;
    product_group: any;
    hs_ver: string;
  }
  product_group?: any; // added this to force re-fetch when product group changes
}


const ByProductLineChartCard: FC<ByProductChartCardProps> = ({
  params,
  product_group = [],
}) => {
  

  const { registerStart, registerEnd } = useLoading();
  const [isLoading, setIsLoading] = useState(true)
  const [usdSeries, setUsdSeries] = useState<any[]>([]);
  const [kgSeries, setKgSeries] = useState<any[]>([]);
  const [years, setYears] = useState<number[]>([]);

  const [mainTitle, subTitle] = useMemo(() => {
    const findPG = (id: number, list: any[]): any | null => {
      for (const item of list) {
        if (item.id === id) return item;
        if (item.sub_category?.length) {
          const found = findPG(id, item.sub_category);
          if (found) return found;
        }
      }
      return null;
    };

    let isDrilldown = false
    let drilldownParentName = ''
    if(params.product_group?.length === 1){
      const selectedPG = findPG(+params.product_group[0], product_group);
      if(selectedPG?.sub_category?.length){
        isDrilldown = true
        drilldownParentName = selectedPG.name
      }
    }

    const flowLabel = params.tradeflow === 'imports' ? 'Import' : 'Export'
    let titleSuffix = ''
    if(isDrilldown){
      titleSuffix = `${drilldownParentName} (Detail)`
    } else {
      const selectedNames = (params.product_group || []).map((pgId: any) => {
        const found = findPG(+pgId, product_group);
        return found?.name || ''
      }).filter(Boolean)
      titleSuffix = selectedNames.length > 0
        ? selectedNames.join(', ')
        : 'Product Group'
    }
    const main = `${getNameByISO3(params.rep) || 'ASEAN'}'s Historical ${flowLabel} Value`
    return [main, titleSuffix];
  }, [params.rep, params.tradeflow, params.product_group, product_group]);

  const graphTitle = `${mainTitle} by ${subTitle}`;

  const baseOptions = useMemo(
    () => ({
      chart: {
        marginTop: 20,
        events: {
          load: function (this: ExtendedHighchartCreditInterface) {
            this.credits.element.onclick = function () {
              window.open("http://comtrade.un.org", "_blank");
            };
          },
        },
      },
      tooltip: { shared: true, crosshairs: true },
      title: { text: "" },
      xAxis: [
        {
          crosshair: true,
          tickInterval: 1,
          categories: years, // will be filled when data arrives
        },
      ],
      yAxis: [
        {
          labels: {
            formatter: function (this: any) {
              return shortenLargeNumber(this.value);
            },
          },
          title: { text: `Value in (USD)` },
        },
      ],
      plotOptions: {
        areaspline: {
            stacking: 'normal',
        },
        series: { connectNulls: true },
      },
      credits: {
        enabled: true,
        text: "Data source: UN Comtrade",
        href: "#",
        position: { align: "center" },
      },
      exporting: { enabled: true },
      accessibility: { enabled: false },
    }),
    [years]
  );
  const usdOptions = useMemo(
    () => ({
      ...baseOptions,
      yAxis: [
        {
          labels: {
            formatter: function (this: any) {
              return shortenLargeNumber(this.value);
            },
          },
          title: { text: `Value (USD)` },
        },
      ],
      series: usdSeries,
    }),
    [baseOptions, usdSeries]
  );
  const kgOptions = useMemo(
    () => ({
      ...baseOptions,
      yAxis: [
        {
          labels: {
            formatter: function (this: any) {
              return shortenLargeNumber(this.value);
            },
          },
          title: { text: `Net weight (KG)` },
        },
      ],
      series: kgSeries,
    }),
    [baseOptions, kgSeries]
  );


  useEffect(()=>{
    if (!params?.rep) return;
    let productGroups = []
    let isDrilldown = false
    let drilldownParentName = ''
    registerStart()
    setIsLoading(true)
    if(params.product_group?.length === 1){
      const selectedPG = product_group.find((pg:any)=>pg.id === +params.product_group[0])
      if(selectedPG?.sub_category?.length){
        isDrilldown = true
        drilldownParentName = selectedPG.name
        productGroups = selectedPG.sub_category.map(x => x.id)
      } else {
        productGroups = params.product_group;
      }
    }else{
      productGroups = params.product_group || []
    }

    // Moved to useMemo: graphTitle
    /*
    const flowLabel = params.tradeflow === 'imports' ? 'Import' : 'Export'
    ...
    */

    Promise.all(
      productGroups?.map((pg:any)=>{
        return getHistoricalTradeData({
          rep: params.rep,
          par:params.par || 'WLD',
          tradeflow:params.tradeflow,
          productGroup:pg,
        })
      })
    ).then((result)=>{
      const usdSeries: any[] = [];
      const kgSeries: any[] = [];

      const flow = params.tradeflow==='imports'?'1':'2'

      result.forEach((res)=>{
        const data = res.data.data
        const metadata = res.data.metadata
        const name =
          metadata.product_group.find((pg: any) => pg.parent_id === null)?.name ??
          metadata.product_group[0]?.name ?? '';
        const tradeData = data.history_data?.[flow] ?? [];

        // Build an index of year -> { val, net }
        const yearIndex = new Map<number, { val: number; net: number }>();
        data.years_data.forEach((y: number) => yearIndex.set(y, { val: 0, net: 0 }));

        tradeData.forEach((row: any) => {
          const y = row.year;
          if (!yearIndex.has(y)) yearIndex.set(y, { val: 0, net: 0 });
          const bucket = yearIndex.get(y)!;

          bucket.val += Number(row.val ?? 0);
          bucket.net += Number(row.netWeight);
        });

        usdSeries.push({
          name,
          data: data.years_data.map((y: number) => ({
            x: y,
            y: yearIndex.get(y)?.val ?? 0,
          })),
          type: 'areaspline',
          tooltip: { valueSuffix: ' US$' },
        });

        kgSeries.push({
          name,
          data: data.years_data.map((y: number) => ({
            x: y,
            y: yearIndex.get(y)?.net ?? 0,
          })),
          type: 'areaspline',
          tooltip: { valueSuffix: ' kg' },
        });
      })
      setUsdSeries(usdSeries)
      setKgSeries(kgSeries)
      // setGraphTitle(...) moved to useMemo
    })
    .catch((err)=>{
      console.error(err)
    })
    .finally(()=>{
      setIsLoading(false)
      registerEnd()
    })
    
  },[params, registerStart, registerEnd])

  return (
    <>
        <Card className="card-custom">
          <>
            <Card.Header className="card-header-stretch d-flex align-items-center justify-content-between">
              <Card.Title
                title={graphTitle}
                style={{
                  maxWidth: '80%',
                }}
              >
                <div className="d-flex flex-column">
                  <span className="fw-bold">{mainTitle}</span>
                  <span className="text-muted fs-7 fw-normal mt-1">by {subTitle}</span>
                </div>
              </Card.Title>
              {/* <TradePartnerDropdown onSelect={handleSelectPar}/> */}
            </Card.Header>
            <Card.Body style={{minHeight:'300px'}}>
              
            {!isLoading ? (
              <Tabs defaultActiveKey="usd" id="trade-tabs">
                <Tab eventKey="usd" title="USD">
                  <div className="mt-4">
                    <ChartWithShare chartOptions={usdOptions} title={graphTitle} />
                  </div>
                </Tab>
                <Tab eventKey="kg" title="KG (Net weight)">
                  <div className="mt-4">
                    <ChartWithShare chartOptions={kgOptions} title={graphTitle} />
                  </div>
                </Tab>
              </Tabs>
            ):(
              <div className="d-flex align-items-center justify-content-center" style={{ height: '50vh' }}>
                <Spinner animation="border" role="status" variant="primary" className="me-3" />
                <div className="loading-text">
                  Loading
                  <span className="dot">.</span>
                  <span className="dot">.</span>
                  <span className="dot">.</span>
                </div>
              </div>
            )}
            </Card.Body>
          </>
        </Card>  
    </>
  )
}

export {ByProductLineChartCard}